"use client";

import { useState, useEffect, useCallback } from "react";

interface NetworkStatus {
    isOnline: boolean;
    wasOffline: boolean; // true for 5s after coming back online
}

/**
 * App-wide network status detection.
 * Listens to browser online/offline events + window focus pings.
 */
export function useNetworkStatus(): NetworkStatus {
    const [isOnline, setIsOnline] = useState(true);
    const [wasOffline, setWasOffline] = useState(false);

    const goOnline = useCallback(() => {
        setIsOnline(true);
        setWasOffline(true);
        // Clear the "was offline" flag after 5s
        setTimeout(() => setWasOffline(false), 5000);
    }, []);

    const goOffline = useCallback(() => {
        setIsOnline(false);
        setWasOffline(false);
    }, []);

    useEffect(() => {
        // Sync with current browser state on mount
        setIsOnline(navigator.onLine);

        window.addEventListener("online", goOnline);
        window.addEventListener("offline", goOffline);

        return () => {
            window.removeEventListener("online", goOnline);
            window.removeEventListener("offline", goOffline);
        };
    }, [goOnline, goOffline]);

    return { isOnline, wasOffline };
}
